/* AMC walk-in build experience.
   Enhances the verified walk-in configurator (amc-walkin-configurator-bridge.js)
   without replacing it: native selects stay the source of truth for pricing,
   rules and cart verification. This layer adds guided steps, tile selectors,
   a live top-view layout, equipment (lifts, racks, boards, trays) and a build bar. */
(() => {
  'use strict';
  if (window.__amcWxLoaded) return;
  window.__amcWxLoaded = true;

  const root = document.querySelector('[data-amc-wx]');
  if (!root) return;

  const fmt = cents => new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(cents / 100);
  const fmtWhole = cents => new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 ? 2 : 0, minimumFractionDigits: cents % 100 ? 2 : 0}).format(cents / 100);
  const el = (tag, cls, html) => {const n = document.createElement(tag); if (cls) n.className = cls; if (html != null) n.innerHTML = html; return n;};
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const parseMoney = t => {const m = String(t || '').replace(/,/g, '').match(/-?\d+(?:\.\d+)?/); return m ? Math.round(Number(m[0]) * 100) : 0;};

  let data = {items: []};
  try {data = JSON.parse(root.querySelector('[data-amc-wx-data]')?.textContent || '{}');} catch (_) {}
  // Defense in depth: old cached JSON and saved equipment cannot restore a
  // five-tier rack into any cooler or freezer build.
  const isFiveTierRack = i => {
    const key = [i.name, i.title, i.handle, i.url].join(' ').replace(/[‑–—_]/g, '-');
    return /rack/i.test(key) && /(?:5|five)[\s-]*tier/i.test(key);
  };
  const items = (data.items || []).filter(i => !isFiveTierRack(i) && Array.isArray(i.variants) && i.variants.length);
  items.forEach(i => i.variants.sort((a, b) => a.price - b.price));
  const productTitle = root.dataset.productTitle || 'Walk-in';
  const unit = root.dataset.unit || 'walk-in';
  const isFreezer = root.dataset.freezer === 'true';

  /* ---------- equipment state ---------- */
  const STORE = 'amcWxEquipment:' + (root.dataset.productHandle || '');
  const state = new Map(); // key -> {variantId, qty}
  try {
    const saved = JSON.parse(localStorage.getItem(STORE) || sessionStorage.getItem(STORE) || '{}');
    items.forEach(item => {
      const s = saved[item.key];
      if (s && s.qty > 0 && item.variants.some(v => v.id === s.variantId && v.available)) state.set(item.key, {variantId: s.variantId, qty: Math.min(s.qty, item.max || 24)});
    });
  } catch (_) {}
  // Save only the compatible selections, removing stale incompatible keys.
  try {localStorage.setItem(STORE, JSON.stringify(Object.fromEntries(state))); sessionStorage.removeItem(STORE);} catch (_) {}
  const save = () => {try {localStorage.setItem(STORE, JSON.stringify(Object.fromEntries(state)));} catch (_) {}};

  const labelMap = item => {
    const map = {};
    String(item.labels || '').split('|').forEach(pair => {const [a, b] = pair.split('='); if (a && b) map[a.trim().toLowerCase()] = b.trim();});
    return map;
  };
  const variantLabel = (item, v) => labelMap(item)[String(v.title).toLowerCase()] || (v.title === 'Default Title' ? '' : v.title);
  const defaultVariant = item => item.variants.filter(v => v.available).sort((a, b) => a.price - b.price)[0] || item.variants[0];
  const current = item => {
    const s = state.get(item.key);
    const v = (s && item.variants.find(x => x.id === s.variantId)) || defaultVariant(item);
    return {variant: v, qty: s ? s.qty : 0};
  };
  const setQty = (item, qty, variantId) => {
    const c = current(item);
    const vid = variantId || c.variant.id;
    qty = Math.max(0, Math.min(item.max || 24, qty | 0));
    if (qty <= 0) state.delete(item.key); else state.set(item.key, {variantId: vid, qty});
    save();
    renderEquipment();
    refresh();
  };
  const equipmentLines = () => items.map(item => {
    const s = state.get(item.key); if (!s || s.qty <= 0) return null;
    const v = item.variants.find(x => x.id === s.variantId); if (!v) return null;
    return {item, variant: v, qty: s.qty};
  }).filter(Boolean);
  const equipmentTotal = () => equipmentLines().reduce((sum, l) => sum + l.variant.price * l.qty, 0);
  const equipmentCount = () => equipmentLines().reduce((sum, l) => sum + l.qty, 0);

  /* ---------- equipment cards ---------- */
  const groupWord = {lift: 'lift', rack: 'rack', board: 'body board', tray: 'tray', table: 'dressing table'};
  function stepper(item, compact) {
    const {qty} = current(item);
    const wrap = el('div', 'amc-wx-qty' + (qty ? ' is-on' : ''));
    if (!qty) {
      const add = el('button', 'amc-wx-add' + (compact ? ' amc-wx-add--mini' : ''), compact ? '+ Add' : 'Add');
      add.type = 'button';
      add.setAttribute('aria-label', `Add ${item.name} to your build`);
      add.addEventListener('click', () => setQty(item, 1));
      wrap.append(add);
      return wrap;
    }
    const minus = el('button', 'amc-wx-qty__btn', '&minus;'); minus.type = 'button'; minus.setAttribute('aria-label', `Remove one ${item.name}`);
    const val = el('input', 'amc-wx-qty__val'); val.type = 'number'; val.min = '0'; val.max = String(item.max || 24); val.value = String(qty); val.inputMode = 'numeric';
    val.setAttribute('aria-label', `${item.name} quantity`);
    const plus = el('button', 'amc-wx-qty__btn', '+'); plus.type = 'button'; plus.setAttribute('aria-label', `Add one ${item.name}`);
    minus.addEventListener('click', () => setQty(item, qty - 1));
    plus.addEventListener('click', () => setQty(item, qty + 1));
    plus.disabled = qty >= (item.max || 24);
    val.addEventListener('change', () => setQty(item, Number(val.value) || 0));
    wrap.append(minus, val, plus);
    return wrap;
  }
  function variantPills(item) {
    if (item.variants.length < 2) return null;
    const {variant, qty} = current(item);
    const pills = el('div', 'amc-wx-vpills');
    pills.setAttribute('role', 'radiogroup');
    pills.setAttribute('aria-label', `${item.name} option`);
    item.variants.forEach(v => {
      const b = el('button', 'amc-wx-vpill' + (v.id === variant.id ? ' is-on' : ''), esc(variantLabel(item, v)));
      b.type = 'button'; b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', String(v.id === variant.id));
      b.disabled = !v.available;
      b.addEventListener('click', () => {
        if (qty > 0) setQty(item, qty, v.id);
        else {state.set(item.key, {variantId: v.id, qty: 0}); renderEquipment();}
      });
      pills.append(b);
    });
    return pills;
  }
  function card(item, compact) {
    const {variant, qty} = current(item);
    const c = el('article', `amc-wx-card${compact ? ' amc-wx-card--mini' : ''}${qty ? ' is-selected' : ''}`);
    c.dataset.key = item.key;
    const media = el('a', 'amc-wx-card__media');
    media.href = item.url; media.target = '_blank'; media.rel = 'noopener';
    media.setAttribute('aria-label', `View ${item.name} details (opens in a new tab)`);
    if (item.image) {const img = el('img'); img.src = item.image; img.alt = ''; img.loading = 'lazy'; img.width = 96; img.height = 96; media.append(img);}
    c.append(media);
    const body = el('div', 'amc-wx-card__body');
    body.append(el('h4', 'amc-wx-card__name', esc(item.name)));
    if (item.spec && !compact) body.append(el('p', 'amc-wx-card__spec', esc(item.spec)));
    if (!compact) {const pills = variantPills(item); if (pills) body.append(pills);}
    const foot = el('div', 'amc-wx-card__foot');
    foot.append(el('span', 'amc-wx-card__price', fmt(variant.price) + (qty > 1 ? `<small> × ${qty}</small>` : '')));
    foot.append(stepper(item, compact));
    body.append(foot);
    c.append(body);
    if (qty) c.append(el('span', 'amc-wx-card__tick', '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'));
    return c;
  }

  let rackTab = null, groupTab = 'lift';
  const GROUPS = [['lift', 'Lifts'], ['rack', 'Racks'], ['board', 'Body boards'], ['tray', 'Trays'], ['table', 'Dressing tables']];
  function renderGroupTabs() {
    const wrap = root.querySelector('.amc-wx__groups');
    if (!wrap) return;
    let barT = root.querySelector('.amc-wx-gtabs');
    if (!barT) {barT = el('div', 'amc-wx-gtabs'); barT.setAttribute('role', 'tablist'); barT.setAttribute('aria-label', 'Equipment type'); wrap.before(barT); wrap.classList.add('is-tabbed');}
    const avail = GROUPS.filter(([g]) => items.some(i => i.group === g));
    if (!avail.some(([g]) => g === groupTab)) groupTab = avail[0]?.[0];
    barT.innerHTML = '';
    avail.forEach(([g, label]) => {
      const n = equipmentLines().filter(l => l.item.group === g).reduce((a, l) => a + l.qty, 0);
      const b = el('button', 'amc-wx-gtab' + (g === groupTab ? ' is-on' : ''), `${esc(label)}${n ? ` <span>${n}</span>` : ''}`);
      b.type = 'button'; b.setAttribute('role', 'tab'); b.setAttribute('aria-selected', String(g === groupTab));
      b.addEventListener('click', () => {groupTab = g; renderEquipment();});
      barT.append(b);
    });
    root.querySelectorAll('.amc-wx-group').forEach(sec => sec.classList.toggle('is-active', sec.dataset.group === groupTab));
  }
  const showAll = new Set();
  /* One-time entrance: equipment cards cascade in and the plan draws in when first scrolled into view (never on re-render) */
  function reveal(node, cls) {
    if (!node || node.dataset.wxRevealed || !('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    node.dataset.wxRevealed = '1';
    const r = node.getBoundingClientRect();
    if (r.top < innerHeight * 0.9) return;
    node.classList.add('wx-pre');
    const io = new IntersectionObserver(es => {
      if (!es.some(e => e.isIntersecting)) return;
      io.disconnect();
      node.classList.remove('wx-pre'); node.classList.add(cls);
      setTimeout(() => node.classList.remove(cls), 1600);
    }, {rootMargin: '0px 0px -12% 0px'});
    io.observe(node);
    setTimeout(() => {if (node.classList.contains('wx-pre')) {node.classList.remove('wx-pre');}}, 8000);
  }
  function renderEquipment() {
    renderGroupTabs();
    ['lift', 'rack', 'board', 'tray', 'table'].forEach(group => {
      const grid = root.querySelector(`[data-amc-wx-grid="${group}"]`);
      if (!grid) return;
      let list = items.filter(i => i.group === group);
      if (!list.length) {grid.closest('.amc-wx-group')?.setAttribute('hidden', ''); return;}
      if (group === 'rack') {
        const tabs = [...new Set(list.map(i => i.tab).filter(Boolean))];
        const bar = root.querySelector('[data-amc-wx-tabs="rack"]');
        if (bar && tabs.length > 1) {
          if (!rackTab) rackTab = 'All';
          bar.innerHTML = '';
          ['All', ...tabs].forEach(t => {
            const count = (t === 'All' ? list : list.filter(i => i.tab === t)).reduce((s, i) => s + current(i).qty, 0);
            const b = el('button', 'amc-wx-tab' + (t === rackTab ? ' is-on' : ''), esc(t) + (count ? ` <span>${count}</span>` : ''));
            b.type = 'button'; b.setAttribute('role', 'tab'); b.setAttribute('aria-selected', String(t === rackTab));
            b.addEventListener('click', () => {rackTab = t; renderEquipment();});
            bar.append(b);
          });
          if (rackTab !== 'All') list = list.filter(i => i.tab === rackTab);
        } else if (bar) bar.hidden = true;
      }
      const focusedKey = document.activeElement?.closest?.('.amc-wx-card')?.dataset.key;
      const focusedCls = document.activeElement?.className;
      grid.innerHTML = '';
      /* 2x2 per category: show four (selected first-in-order), the rest behind "Show all" */
      const CAP = 4;
      let shown = list;
      if (list.length > CAP && !showAll.has(group)) {
        const pick = new Set(list.filter(i => current(i).qty).slice(0, CAP));
        for (const i of list) {if (pick.size >= CAP) break; pick.add(i);}
        shown = list.filter(i => pick.has(i));
      }
      shown.forEach((item, n) => {const c = card(item, false); c.style.setProperty('--wx-i', n); grid.append(c);});
      let more = grid.nextElementSibling?.classList?.contains('amc-wx-more') ? grid.nextElementSibling : null;
      if (list.length > CAP) {
        if (!more) {more = el('button', 'amc-wx-more'); more.type = 'button'; grid.after(more);}
        const open = showAll.has(group);
        more.innerHTML = open ? 'Show fewer' : `Show all ${list.length} <span aria-hidden="true">&rsaquo;</span>`;
        more.setAttribute('aria-expanded', String(open));
        more.onclick = () => {open ? showAll.delete(group) : showAll.add(group); renderEquipment(); if (open) grid.scrollIntoView({block: 'nearest', behavior: 'smooth'});};
      } else if (more) more.remove();
      if (focusedKey && grid.contains(document.activeElement) === false) {
        const target = grid.querySelector(`.amc-wx-card[data-key="${focusedKey}"]`);
        const f = target && (target.querySelector('.' + String(focusedCls).split(' ')[0]) || target.querySelector('button'));
        if (f && root.contains(f)) f.focus({preventScroll: true});
      }
    });
    renderStrip();
    renderTotals();
    reveal(root.querySelector('.amc-wx__groups'), 'wx-reveal');
    try {if (plan && plan.isConnected) reveal(plan, 'wx-draw');} catch (_) {}
    window.dispatchEvent(new CustomEvent('amc:wx-equipment', {detail: {count: equipmentCount(), total: equipmentTotal()}}));
  }

  const PLUS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  const TICK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  let justAdded = null;
  function miniCard(item) {
    const {variant, qty} = current(item);
    const c = el('article', 'amc-wx-mini' + (qty ? ' is-selected' : '') + (justAdded === item.key ? ' is-pop' : ''));
    c.dataset.key = item.key;
    c.innerHTML = `<span class="amc-wx-mini__img">${item.image ? `<img src="${esc(item.image)}" alt="" loading="lazy" width="56" height="56">` : ''}${qty ? `<span class="amc-wx-mini__tick">${TICK}</span>` : ''}</span><span class="amc-wx-mini__body"><span class="amc-wx-mini__name">${esc(item.name)}</span><span class="amc-wx-mini__row"><span class="amc-wx-mini__price">${fmt(variant.price)}</span></span></span>`;
    const row = c.querySelector('.amc-wx-mini__row');
    if (!qty) {
      const add = el('button', 'amc-wx-mini__add', PLUS);
      add.type = 'button';
      add.setAttribute('aria-label', `Add ${item.name} to your build, ${fmt(variant.price)}`);
      c.addEventListener('click', () => {justAdded = item.key; setQty(item, 1); setTimeout(() => {justAdded = null;}, 600);});
      row.append(add);
    } else {
      const q = el('div', 'amc-wx-mini__qty');
      q.setAttribute('role', 'group');
      q.setAttribute('aria-label', `${item.name} quantity`);
      const minus = el('button', '', qty === 1 ? '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>' : '&minus;');
      minus.type = 'button'; minus.setAttribute('aria-label', qty === 1 ? `Remove ${item.name}` : `Remove one ${item.name}`);
      const n = el('span', '', String(qty)); n.setAttribute('aria-live', 'polite');
      const plus = el('button', '', '+'); plus.type = 'button'; plus.setAttribute('aria-label', `Add one ${item.name}`);
      plus.disabled = qty >= (item.max || 24);
      minus.addEventListener('click', e => {e.stopPropagation(); setQty(item, qty - 1);});
      plus.addEventListener('click', e => {e.stopPropagation(); setQty(item, qty + 1);});
      q.append(minus, n, plus);
      row.append(q);
    }
    return c;
  }
  function renderStrip() {
    const track = document.querySelector('[data-amc-wx-strip-track]');
    if (!track) return;
    const active = document.activeElement;
    const focusedKey = active?.closest?.('.amc-wx-mini')?.dataset.key;
    const focusedIdx = focusedKey ? [...active.closest('.amc-wx-mini').querySelectorAll('button')].indexOf(active) : -1;
    const inStrip = track.contains(active);
    track.innerHTML = '';
    items.filter(i => i.strip).forEach(item => track.append(miniCard(item)));
    if (inStrip && focusedKey) {
      const btns = track.querySelectorAll(`.amc-wx-mini[data-key="${focusedKey}"] button`);
      (btns[Math.min(Math.max(focusedIdx, 0), btns.length - 1)] || btns[0])?.focus({preventScroll: true});
    }
  }

  function renderTotals() {
    const box = root.querySelector('[data-amc-wx-total]');
    const count = equipmentCount(), total = equipmentTotal();
    if (box) {
      box.hidden = !count;
      box.querySelector('[data-amc-wx-total-count]').textContent = `${count} equipment ${count === 1 ? 'item' : 'items'} selected`;
      box.querySelector('[data-amc-wx-total-amount]').textContent = fmt(total);
    }
    updateBuild();
  }

  /* ---------- cart: add equipment after the verified build ---------- */
  function wrapCart() {
    const original = window.AMCWalkinCart;
    if (!original || original.__amcWx) return !!original;
    const verify = async lines => {
      for (const l of lines) {
        let v;
        try {
          const r = await fetch(`${(window.Shopify?.routes?.root || '/')}variants/${l.variant.id}.js`, {credentials: 'same-origin', headers: {Accept: 'application/json'}});
          if (!r.ok) throw new Error('x');
          v = await r.json();
        } catch (_) {
          throw Object.assign(new Error(`${l.item.name} could not be checked. Nothing was added. Remove it from your equipment or try again.`), {code: 'EQUIPMENT_VERIFY'});
        }
        if (v.available !== true) throw Object.assign(new Error(`${l.item.name} is currently unavailable. Nothing was added. Remove it from your equipment to continue.`), {code: 'EQUIPMENT_UNAVAILABLE'});
        if (v.price !== l.variant.price) throw Object.assign(new Error(`The current price for ${l.item.name} is ${fmt(v.price)}. Nothing was added. Reload the page to confirm updated pricing.`), {code: 'EQUIPMENT_PRICE'});
      }
    };
    const add = async (config = {}) => {
      const lines = equipmentLines();
      const status = t => {try {config.onStatus?.(t);} catch (_) {}};
      if (lines.length) {status('Checking equipment…'); await verify(lines);}
      const result = await original.add(config);
      if (lines.length) {
        status('Adding your equipment…');
        try {
          const r = await fetch(`${(window.Shopify?.routes?.root || '/')}cart/add.js`, {
            method: 'POST', credentials: 'same-origin',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({items: lines.map(l => ({id: l.variant.id, quantity: l.qty, properties: {'Added with': productTitle}}))})
          });
          if (!r.ok) throw new Error('rejected');
          state.clear(); save();
        } catch (_) {
          throw Object.assign(new Error('Your walk-in build was added. The selected equipment could not be confirmed, so review your cart before adding it again.'), {cartReviewRequired: true});
        }
      }
      return result;
    };
    window.AMCWalkinCart = Object.freeze({add, __amcWx: true});
    return true;
  }

  /* ---------- configurator enhancement: one question at a time ---------- */
  const ORDER = ['bRef', 'bLoc', 'bHinge', 'bDoor', 'bHandle', 'bPlate', 'bFloor', 'bRamp', 'bLed'];
  const CHECK = '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9"/><path d="M5.8 10.4l2.9 2.9 5.5-6.2"/></svg>';
  let builder, info, plan, bar, summary, rows = [], tilesBySelect = new Map();
  const confirmed = new Set();
  let openCat = null, justDone = null;
  const isPlaceholderOpt = o => !o || o.dataset.amcPlaceholder !== undefined || (o.value === '' && /^select\b/i.test(o.textContent.trim()));
  function parseOption(select, opt) {
    let t = opt.textContent.replace(/\s+/g, ' ').trim();
    let note = '';
    if (/requires factory confirmation|factory confirmation/i.test(t) || opt.dataset.amcQuoteRequired === 'true') {
      note = 'Factory confirmation';
      t = t.replace(/[—–-]?\s*[^—–]*factory confirmation[^)]*/i, '').trim();
    }
    let price = '';
    const p = Number(opt.dataset.p || 0);
    const amounts = [...t.matchAll(/\$\s?([\d,]+(?:\.\d{1,2})?)/g)].map(m => Math.round(Number(m[1].replace(/,/g, '')) * 100));
    if (select.id === 'bPlate' && amounts.length >= 2) {
      const door = builder.querySelector('#bDoor');
      const wide = door && /48/.test(door.selectedOptions[0]?.textContent || '');
      price = '+' + fmtWhole(wide ? amounts[1] : amounts[0]);
    } else if (p > 0) price = '+' + fmtWhole(Math.round(p * 100));
    else if (amounts.length && !/included/i.test(t)) price = '+' + fmtWhole(amounts[0]);
    else if (/included/i.test(t)) price = 'Included';
    let sub = '';
    const paren = t.match(/\(([^)]*)\)/);
    if (paren) {sub = paren[1]; t = t.replace(paren[0], ' ');}
    t = t.replace(/[,—–]?\s*(?:add\s*)?\+?\$\s?[\d,]+(?:\.\d{1,2})?(?:\s*\/\s*\$?[\d,]+(?:\.\d{1,2})?)?/gi, ' ')
      .replace(/[,—–]\s*included\b/i, '').replace(/\bincluded\b/i, '')
      .replace(/^yes,\s*/i, '').replace(/^add\s+/i, '').replace(/\s+([,·])/g, '$1').replace(/\s{2,}/g, ' ').replace(/[,\s—–-]+$/, '').trim();
    sub = sub.replace(/\$\s?[\d,]+(?:\.\d{1,2})?/g, '').replace(/\+|\badd\b/gi, '').replace(/\s{2,}/g, ' ').replace(/^[\s,·—–-]+|[\s,·—–-]+$/g, '').trim();
    if (select.id === 'bPlate' && /door/i.test(sub) && amounts.length >= 2) sub = 'Lower door kick plate';
    const parts = t.split(/,\s*/);
    let name = parts.shift() || t;
    if (parts.length) sub = [parts.join(' · '), sub].filter(Boolean).join(' · ');
    if (/^no floor/i.test(name) && !sub) sub = 'Floorless standard';
    sub = sub.replace(/^floorless standard$/i, 'Floorless standard');
    if (note) sub = [sub, note].filter(Boolean).join(' · ');
    return {name: name.trim(), sub, price};
  }

  /* Compact option details do not change native values or transaction data. */
  let optionInfoBox, optionInfoTrigger;
  function closeOptionInfo(focus = false) {
    const trigger = optionInfoTrigger;
    if (trigger) {trigger.setAttribute('aria-expanded', 'false'); trigger.removeAttribute('aria-describedby');}
    if (optionInfoBox) optionInfoBox.hidden = true;
    optionInfoTrigger = null;
    if (focus && trigger?.isConnected) trigger.focus({preventScroll: true});
  }
  function makeOptionInfo(name, detail) {
    const button = el('button', 'amc-wx-option-info', '<span aria-hidden="true">i</span>');
    button.type = 'button';
    button.setAttribute('aria-label', 'About ' + name);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-haspopup', 'dialog');
    button.addEventListener('click', ev => {
      ev.stopPropagation();
      if (optionInfoTrigger === button) {closeOptionInfo(); return;}
      closeOptionInfo();
      if (!optionInfoBox) {
        optionInfoBox = el('div', 'amc-wx-option-pop');
        optionInfoBox.id = 'amc-wx-option-details';
        optionInfoBox.setAttribute('role', 'dialog');
        optionInfoBox.setAttribute('aria-label', 'Option details');
        optionInfoBox.innerHTML = '<button type="button" aria-label="Close option details">×</button><p id="amc-wx-option-detail-copy"></p>';
        document.body.append(optionInfoBox);
        optionInfoBox.querySelector('button').addEventListener('click', () => closeOptionInfo(true));
        document.addEventListener('pointerdown', e => {if (!optionInfoBox.contains(e.target) && !e.target.closest('.amc-wx-option-info')) closeOptionInfo();});
        document.addEventListener('keydown', e => {if (e.key === 'Escape' && optionInfoTrigger) {e.preventDefault(); closeOptionInfo(true);}});
        window.addEventListener('resize', () => closeOptionInfo());
        window.addEventListener('scroll', () => closeOptionInfo(), {passive: true});
      }
      optionInfoTrigger = button;
      button.setAttribute('aria-controls', optionInfoBox.id);
      button.setAttribute('aria-describedby', 'amc-wx-option-detail-copy');
      button.setAttribute('aria-expanded', 'true');
      optionInfoBox.querySelector('p').textContent = detail;
      optionInfoBox.hidden = false;
      const r = button.getBoundingClientRect(), b = optionInfoBox.getBoundingClientRect();
      optionInfoBox.style.left = Math.max(10, Math.min(r.right - b.width, innerWidth - b.width - 10)) + 'px';
      optionInfoBox.style.top = Math.max(10, r.bottom + b.height + 8 < innerHeight ? r.bottom + 6 : r.top - b.height - 6) + 'px';
    });
    return button;
  }
  function compactOption(select, opt, name, sub) {
    let detail = opt.textContent.replace(/\s+/g, ' ').trim();
    if (select.id === 'bRef' || select.id === 'r') {
      const level = detail.match(/level\s*(\d)/i), ambient = detail.match(/\d+\s*°F/i);
      const quote = opt.dataset.amcQuoteRequired === 'true' || /factory confirmation/i.test(detail);
      name = level ? `${quote ? 'L' : 'Level '}${level[1]}${ambient ? ' · ' + ambient[0] : ''}${quote ? ' · RFQ' : ''}` : 'Standard remote';
      detail = `${level ? 'Level ' + level[1] : 'Standard'} remote two-piece system${ambient ? ' · ' + ambient[0] + ' ambient' : ', included'}.`;
      if (quote) detail += ' Factory confirmation is required before ordering.';
      sub = '';
    } else if (select.id === 'bLoc') {
      const dimension = name.match(/\d+(?:\.\d+)?\s*[′'’]/);
      const wall = /front/i.test(name) ? 'Front wall' : /side/i.test(name) ? 'Side wall' : dimension ? dimension[0].replace(/\s/g, '') + ' wall' : name;
      name = wall + (/off center/i.test(name) ? ' · offset' : ' · corner');
    }
    else if (select.id === 'bDoor') name = /48/.test(name) ? '48″ wide door' : '36″ standard door';
    else if (select.id === 'bHandle') name = /lockable/i.test(name) ? 'Lockable handle' : 'Standard handle';
    else if (select.id === 'bPlate') name = /^no /i.test(name) ? 'No diamond plate' : 'Diamond plate';
    else if (select.id === 'bFloor' || select.id === 'f') {
      name = /^no /i.test(name) ? 'No floor' : 'Insulated floor';
      detail += '. A solid, flat, level supporting surface is required. The floor is not a leveling system. Ramp sold separately.';
    } else if (select.id === 'bRamp') {
      name = /^no /i.test(name) ? 'No ramp' : '36″ external ramp';
      detail += '. Requires an insulated floor and the 36-inch standard door.';
    } else if (select.id === 'bHinge') detail += '. Confirm opening direction before ordering; hinge selection cannot be changed after the order is placed.';
    if (sub && !detail.includes(sub)) detail += ' ' + sub + '.';
    return {name, detail};
  }

  /* ---------- LED lighting: any fixture count, priced per 4-ft fixture ---------- */
  const LED_IDS = {1: 52289540030766, 2: 52289540063534, 3: 52289432650030, 4: 52289432682798, 5: 52289432715566, 6: 52289432748334, 7: 52289432781102};
  const LED_EACH = 159;
  const LED_MAX = 7;
  const ledCount = sel => Number(sel?.selectedOptions?.[0]?.dataset.amcLedN || 0);
  function expandLed() {
    const sel = builder.querySelector('#bLed');
    if (!sel || sel.dataset.amcLedExpanded) return;
    const known = Object.values(LED_IDS).map(String);
    if (![...sel.options].some(o => known.includes(o.value))) return;
    const prev = [...sel.options].find(o => o.selected && known.includes(o.value));
    const prevN = prev ? Number(Object.keys(LED_IDS).find(k => String(LED_IDS[k]) === prev.value)) : 0;
    const none = new Option('No LED lighting', '');
    none.dataset.p = '0'; none.dataset.amcLedN = '0';
    const opts = [none];
    for (let n = 1; n <= LED_MAX; n++) {
      const o = new Option(`${n} × 4-ft LED fixture${n > 1 ? 's' : ''}, Add ${fmt(n * LED_EACH * 100)}`, String(LED_IDS[n]));
      o.dataset.p = String(n * LED_EACH); o.dataset.amcLedN = String(n);
      opts.push(o);
    }
    sel.replaceChildren(...opts);
    sel.selectedIndex = prevN;
    sel.dataset.amcLedExpanded = 'true';
    sel.dispatchEvent(new Event('change', {bubbles: true}));
  }
  function renderLed(select, host) {
    const n = ledCount(select);
    const any = !isPlaceholderOpt(select.selectedOptions[0]);
    const sig = 'led:' + n + ':' + any + ':' + confirmed.has('bLed');
    if (host.dataset.sig === sig) return;
    host.dataset.sig = sig;
    host.classList.add('amc-wx-tiles--led');
    const pending = Number(host.dataset.pending || (n || 2));
    const shown = n || pending;
    host.innerHTML = `<button type="button" class="amc-wx-tile${any && n === 0 && confirmed.has('bLed') ? ' is-on' : ''}" data-led="none"><span class="amc-wx-tile__dot" aria-hidden="true"></span><span class="amc-wx-tile__txt"><span class="amc-wx-tile__name">No LED lighting</span><span class="amc-wx-tile__sub">Standard interior</span></span></button>
      <div class="amc-wx-led${n ? ' is-on' : ''}">
        <div class="amc-wx-led__txt"><span class="amc-wx-tile__name">4-ft LED fixtures</span><span class="amc-wx-tile__sub">${fmtWhole(LED_EACH * 100)} each</span></div>
        <div class="amc-wx-led__ctl"><button type="button" class="amc-wx-led__b" data-led="-" aria-label="One fewer fixture"${shown <= 1 ? ' disabled' : ''}>−</button><output class="amc-wx-led__n" aria-live="polite">${shown}</output><button type="button" class="amc-wx-led__b" data-led="+" aria-label="One more fixture"${shown >= LED_MAX ? ' disabled' : ''}>+</button></div>
        <button type="button" class="amc-wx-led__go" data-led="use">${n && confirmed.has('bLed') ? 'Update' : 'Add'} ${shown} · ${fmtWhole(shown * LED_EACH * 100)}</button>
      </div>`;
    host.querySelector('.amc-wx-led__txt').append(makeOptionInfo('4-ft LED fixtures', 'energy-efficient 4-ft LED fixtures. Priced per fixture; select the quantity, then Add. The selected fixtures are shown to scale on the plan sketch.'));
    const setN = k => {
      k = Math.max(1, Math.min(LED_MAX, k));
      host.dataset.pending = String(k);
      if (n) {select.selectedIndex = k; select.dispatchEvent(new Event('change', {bubbles: true}));}
      host.dataset.sig = '';
      renderLed(select, host);
      if (!n) drawPlan(k);
      host.querySelector(`[data-led="${k >= LED_MAX ? '-' : '+'}"]`)?.focus({preventScroll: true});
    };
    host.querySelector('[data-led="none"]').addEventListener('click', () => {host.dataset.pending = ''; choose(select, 0);});
    host.querySelector('[data-led="-"]').addEventListener('click', () => setN(shown - 1));
    host.querySelector('[data-led="+"]').addEventListener('click', () => setN(shown + 1));
    host.querySelector('[data-led="use"]').addEventListener('click', () => choose(select, shown));
  }
  /* an insulated floor always uses the 36-inch standard door */
  const floorOn = () => {const f = builder.querySelector('#bFloor,#f'); return !!f && !isPlaceholderOpt(f.selectedOptions[0]) && (Number(f.selectedOptions[0]?.dataset.p) > 0 || f.selectedOptions[0]?.dataset.amcFloor === 'true');};
  function enforceFloorDoor() {
    const door = builder.querySelector('#bDoor');
    if (!door || !floorOn()) return false;
    if (!/48/.test(door.selectedOptions[0]?.textContent || '')) return false;
    const std = [...door.options].findIndex(o => !isPlaceholderOpt(o) && /36/.test(o.textContent));
    if (std < 0) return false;
    door.selectedIndex = std;
    door.dispatchEvent(new Event('input', {bubbles: true}));
    door.dispatchEvent(new Event('change', {bubbles: true}));
    confirmed.add('bDoor');
    note('Your door is set to the 36″ standard door, which is used with an insulated floor.');
    return true;
  }
  function note(msg) {
    const host = qbox || builder;
    let n = host.querySelector('.amc-wx-note');
    if (!n) {n = el('p', 'amc-wx-note'); n.setAttribute('role', 'status'); host.append(n);}
    n.textContent = msg; n.hidden = false;
    clearTimeout(note.t); note.t = setTimeout(() => {n.hidden = true;}, 6000);
  }

  const FLOOR_TERMS = 'By selecting No floor, the buyer confirms and accepts that: (1) the walk-in will be installed on a solid, flat, level concrete slab with no cracks, dips, humps or other imperfections; (2) the installation is at ground level and not on a second story, mezzanine or any floor above a basement, crawl space or occupied space, where an insulated floor is required; and (3) condensation, frost, sealing or leveling issues caused by a site that does not meet these conditions are the buyer\'s responsibility.';
  function floorTerms(select, host) {
    // floorless terms live only behind the "i" beside the Insulated floor label
    host.parentNode?.querySelectorAll(':scope > .amc-wx-terms:not(.amc-wx-terms--pop)').forEach(n => n.remove());
    const label = builder.querySelector(`label[for="${select.id}"]`);
    if (!label) return;
    const known = label.querySelector('.amc-floor-info');
    const note = known && document.getElementById(known.getAttribute('aria-controls'));
    if (note) {
      label.querySelector('.amc-wx-info')?.remove();
      document.getElementById('amc-wx-ft-' + select.id)?.remove();
      if (!note.querySelector('.amc-wx-floor-terms')) {
        const t = el('span', 'amc-wx-floor-terms');
        t.innerHTML = `<strong>Floorless installation terms</strong>${esc(FLOOR_TERMS)}`;
        note.append(t);
      }
      return;
    }
    if (label.querySelector('.amc-wx-info')) return;
    const pop = el('div', 'amc-wx-terms amc-wx-terms--pop');
    pop.id = 'amc-wx-ft-' + select.id;
    pop.setAttribute('role', 'note');
    pop.hidden = true;
    pop.innerHTML = `<strong>Floorless installation terms</strong><p>${esc(FLOOR_TERMS)}</p>`;
    const b = el('button', 'amc-wx-info');
    b.type = 'button';
    b.textContent = 'i';
    b.setAttribute('aria-label', 'Floorless installation terms');
    b.setAttribute('aria-expanded', 'false');
    b.setAttribute('aria-controls', pop.id);
    const set = open => {pop.hidden = !open; b.setAttribute('aria-expanded', String(open));};
    b.addEventListener('click', e => {e.preventDefault(); e.stopPropagation(); set(pop.hidden);});
    document.addEventListener('keydown', e => {if (e.key === 'Escape' && !pop.hidden) {set(false); b.focus();}});
    label.append(b);
    label.after(pop);
    [600, 1500, 3000].forEach(ms => setTimeout(() => floorTerms(select, host), ms));
  }
  function renderTiles(select) {
    const heading = builder.querySelector(`label[for="${select.id}"]`);
    const shortHeading = {bFloor: 'Insulated floor', f: 'Insulated floor', bRamp: 'External ramp'}[select.id];
    if (shortHeading && heading?.firstChild?.nodeType === 3 && heading.firstChild.nodeValue.trim() !== shortHeading) heading.firstChild.nodeValue = shortHeading;
    let host = tilesBySelect.get(select);
    if (!host) {
      host = el('div', 'amc-wx-tiles');
      host.setAttribute('role', 'radiogroup');
      const label = builder.querySelector(`label[for="${select.id}"]`);
      if (label) {if (!label.id) label.id = 'amc-wx-l-' + select.id; host.setAttribute('aria-labelledby', label.id);}
      select.after(host);
      select.classList.add('amc-wx-native');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      tilesBySelect.set(select, host);
    }
    if (select.id === 'bFloor' || select.id === 'f') floorTerms(select, host);
    if (select.id === 'bLed' && select.dataset.amcLedExpanded) {renderLed(select, host); return;}
    let opts = [...select.options].filter(o => !isPlaceholderOpt(o) && !o.hidden);
    host.classList.add('amc-wx-tiles--compact');
    if (select.id === 'bRef' || select.id === 'r') {
      // keep refrigeration light: standard, Level 1 and Level 2 (a saved higher level still shows)
      const keep = opts.slice(0, 3);
      const cur = select.selectedOptions[0];
      if (cur && !isPlaceholderOpt(cur) && opts.includes(cur) && !keep.includes(cur)) keep.push(cur);
      opts = keep;
    }
    host.dataset.count = String(opts.length);
    host.classList.toggle('amc-wx-tiles--list', false);
    host.classList.toggle('amc-wx-tiles--three', (select.id === 'bRef' || select.id === 'r') && opts.length === 3);
    host.classList.toggle('amc-wx-tiles--wide', opts.length > 4 && select.id !== 'bRef' && select.id !== 'r');
    const sig = JSON.stringify([select.selectedIndex, select.disabled, opts.map(o => [o.index, o.textContent, o.disabled]), builder.querySelector('#bDoor')?.selectedIndex, select.id === 'bDoor' && floorOn()]);
    if (host.dataset.sig === sig) return;
    host.dataset.sig = sig;
    const hadFocus = host.contains(document.activeElement);
    host.innerHTML = '';
    const selectedIdx = isPlaceholderOpt(select.selectedOptions[0]) ? -1 : select.selectedIndex;
    opts.forEach((o, i) => {
      let {name, sub, price} = parseOption(select, o);
      if (select.id === 'bDoor' && floorOn() && /48/.test(o.textContent)) sub = 'Not used with an insulated floor';
      const compact = compactOption(select, o, name, sub);
      const choice = el('div', 'amc-wx-choice');
      const on = o.index === selectedIdx;
      const b = el('button', 'amc-wx-tile' + (on ? ' is-on' : ''));
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on || (selectedIdx === -1 && i === 0) ? 0 : -1;
      const floorBlocks = select.id === 'bDoor' && floorOn() && /48/.test(o.textContent);
      b.disabled = select.disabled || o.disabled || floorBlocks;
      b.dataset.index = String(o.index);
      b.setAttribute('aria-label', o.textContent.replace(/\s+/g, ' ').trim() + (floorBlocks ? '. Not used with an insulated floor.' : ''));
      b.innerHTML = `<span class="amc-wx-tile__dot" aria-hidden="true"></span><span class="amc-wx-tile__txt"><span class="amc-wx-tile__name">${esc(compact.name)}</span></span>${price ? `<span class="amc-wx-tile__price${price === 'Included' ? ' is-inc' : ''}">${esc(price)}</span>` : ''}`;
      b.addEventListener('click', () => choose(select, o.index));
      b.addEventListener('keydown', e => {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
        if (!keys.includes(e.key)) return;
        e.preventDefault();
        const all = [...host.querySelectorAll('.amc-wx-tile:not(:disabled)')];
        const at = all.indexOf(b);
        const next = all[(at + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length];
        next?.focus(); next?.click();
      });
      choice.append(b, makeOptionInfo(compact.name, compact.detail));
      host.append(choice);
    });
    if (hadFocus) (host.querySelector('.amc-wx-tile.is-on') || host.querySelector('.amc-wx-tile'))?.focus({preventScroll: true});
  }

  function choose(select, index) {
    closeOptionInfo();
    const row = select.closest('.amc-wx-q');
    const cat = row?.dataset.amcCategory;
    const changed = !(select.selectedIndex === index && !isPlaceholderOpt(select.selectedOptions[0]));
    if (changed) {
      select.selectedIndex = index;
      select.dispatchEvent(new Event('input', {bubbles: true}));
      select.dispatchEvent(new Event('change', {bubbles: true}));
    }
    if (cat) {confirmed.add(cat); justDone = cat; openCat = null;}
    setTimeout(() => {refresh(); focusOpen();}, 0);
    setTimeout(refresh, 80);
  }
  function focusOpen() {
    const open = builder.querySelector('.amc-wx-q.is-open');
    if (!open) return;
    const r = open.getBoundingClientRect();
    if (r.top < 70 || r.top > window.innerHeight - 160) open.scrollIntoView({behavior: 'smooth', block: 'center'});
    setTimeout(() => (open.querySelector('.amc-wx-tile.is-on') || open.querySelector('.amc-wx-tile:not(:disabled)'))?.focus({preventScroll: true}), 250);
  }

  const NAMES = {bRef: 'Refrigeration', r: 'Refrigeration', bLoc: 'Door location', bHinge: 'Door hinge', bDoor: 'Door size', bHandle: 'Door handle', bPlate: 'Diamond plate', bFloor: 'Insulated floor', f: 'Insulated floor', bRamp: 'Ramp', bLed: 'LED lighting', l: 'LED lighting'};
  const rowSkipped = row => {
    if (row.hidden || row.closest('[hidden]')) return true;
    const sel = row.querySelector('select');
    if (!sel) return true;
    const wrap = sel.closest('#bRampWrap');
    if (wrap && getComputedStyle(wrap).display === 'none') return true;
    return [...sel.options].filter(o => !isPlaceholderOpt(o)).length === 0;
  };
  const hide = n => n && n.style.setProperty('display', 'none', 'important');
  let qbox, buildBox, shown = new Set();
  function buildFlow() {
    const cats = builder.querySelector('.amc-walkin-categories');
    if (!cats || cats.dataset.amcWx) return;
    cats.dataset.amcWx = 'true';
    cats.classList.add('amc-wx-flow');
    const all = [...cats.querySelectorAll(':scope > .amc-walkin-category')];
    all.sort((x, y) => ORDER.indexOf(x.dataset.amcCategory) - ORDER.indexOf(y.dataset.amcCategory)).forEach(r => cats.append(r));
    all.forEach(row => {
      if (!row.querySelector('select')) {row.classList.add('amc-wx-hide'); return;}
      row.classList.add('amc-wx-q');
      rows.push(row);
    });
    qbox = el('div', 'amc-wx-qbox');
    qbox.innerHTML = `<div class="amc-wx-qbox__top"><span class="amc-wx-qbox__step" data-wx="qstep"></span><button type="button" class="amc-wx-qbox__back" data-wx="back">&larr; Back</button></div><div class="amc-wx-qbox__prog" aria-hidden="true"><span data-wx="prog"></span></div>`;
    cats.before(qbox);
    qbox.append(cats);
    const done = el('div', 'amc-wx-done');
    done.hidden = true;
    done.innerHTML = `<span class="amc-wx-done__check">${CHECK}</span><div class="amc-wx-done__txt"><strong>Your ${esc(unit)} is configured</strong><span>Ready for cart, quote or PO. Equipment is optional.</span></div><button type="button" class="amc-wx-done__btn">Choose equipment</button>`;
    done.querySelector('button').addEventListener('click', () => root.scrollIntoView({behavior: 'smooth', block: 'start'}));
    qbox.append(done);
    qbox.querySelector('[data-wx="back"]').addEventListener('click', () => {
      const live = rows.filter(r => !rowSkipped(r));
      const cur = live.findIndex(r => r.classList.contains('is-open'));
      const prev = cur === -1 ? live[live.length - 1] : live[cur - 1];
      if (prev) {openCat = prev.dataset.amcCategory; refresh(); focusOpen();}
    });
    buildBox = el('section', 'amc-wx-build');
    buildBox.setAttribute('aria-label', 'Your build');
    buildBox.innerHTML = `<div class="amc-wx-build__head"><h3 class="amc-wx-build__title">Your build</h3></div><section class="amc-wx-included" aria-label="Automatically included with every AMC walk-in"><h4>Included with your AMC walk-in</h4><ul><li><span aria-hidden="true">${CHECK}</span>Interior emergency-release handle</li><li><span aria-hidden="true">${CHECK}</span>15-year panel warranty</li><li><span aria-hidden="true">${CHECK}</span>5-year compressor parts warranty</li></ul><p>No added charge. Warranty terms apply.</p></section><ol class="amc-wx-build__lines" data-wx="lines"></ol><div class="amc-wx-build__saving" data-wx="saving" hidden></div><div class="amc-wx-build__total"><span>Build total</span><strong data-wx="total"></strong></div>`;
    qbox.after(buildBox);
  }

  function lineNode(key, name, value, price, onEdit) {
    const li = el('li', 'amc-wx-line' + (shown.has(key) ? '' : ' is-new'));
    li.innerHTML = `<span class="amc-wx-line__check">${CHECK}</span><span class="amc-wx-line__name">${esc(name)}</span><span class="amc-wx-line__val">${esc(value)}</span><span class="amc-wx-line__price">${esc(price)}</span>`;
    const edit = el('button', 'amc-wx-line__edit', 'Edit');
    edit.type = 'button';
    edit.setAttribute('aria-label', `Edit ${name}`);
    edit.addEventListener('click', onEdit);
    li.append(edit);
    shown.add(key);
    return li;
  }

  function refresh() {
    if (!builder || !qbox) return;
    rows.forEach(r => renderTiles(r.querySelector('select')));
    const live = rows.filter(r => !rowSkipped(r));
    rows.forEach(r => r.classList.toggle('amc-wx-skip', rowSkipped(r)));
    live.forEach(r => {const sel = r.querySelector('select'); if (isPlaceholderOpt(sel.selectedOptions[0])) confirmed.delete(r.dataset.amcCategory);});
    let open = openCat && live.find(r => r.dataset.amcCategory === openCat);
    if (!open) {openCat = null; open = live.find(r => !confirmed.has(r.dataset.amcCategory));}
    live.forEach(r => r.classList.toggle('is-open', r === open));
    rows.filter(r => !live.includes(r)).forEach(r => r.classList.remove('is-open'));
    const idx = open ? live.indexOf(open) : live.length;
    const doneCount = live.filter(r => confirmed.has(r.dataset.amcCategory)).length;
    qbox.querySelector('[data-wx="qstep"]').textContent = open ? `Step ${idx + 1} of ${live.length}` : 'Build complete';
    qbox.querySelector('[data-wx="back"]').hidden = idx === 0;
    qbox.querySelector('[data-wx="prog"]').style.width = `${live.length ? Math.round(doneCount / live.length * 100) : 0}%`;
    qbox.querySelector('.amc-wx-done').hidden = !!open;
    qbox.classList.toggle('is-complete', !open);
    // Optional equipment follows a completed cooler/freezer configuration.
    // Keep selections intact when revisiting a configuration step.
    root.hidden = !!open;
    root.style.setProperty('display', open ? 'none' : 'block', 'important');
    // populate the build box
    const list = buildBox.querySelector('[data-wx="lines"]');
    const keep = new Set();
    list.innerHTML = '';
    live.forEach(r => {
      const cat = r.dataset.amcCategory, sel = r.querySelector('select');
      if (!confirmed.has(cat) || isPlaceholderOpt(sel.selectedOptions[0])) return;
      const o = parseOption(sel, sel.selectedOptions[0]);
      keep.add(cat);
      list.append(lineNode(cat, NAMES[cat] || cat, o.name, o.price === 'Included' ? 'Included' : (o.price || '—'), () => {openCat = cat; refresh(); focusOpen();}));
    });
    equipmentLines().forEach(l => {
      const key = 'eq:' + l.item.key;
      keep.add(key);
      const vl = l.item.variants.length > 1 && !/^Default/i.test(l.variant.title || '') ? l.variant.title : '';
      list.append(lineNode(key, `${l.qty} × ${l.item.name}`, vl, '+' + fmt(l.variant.price * l.qty), () => root.scrollIntoView({behavior: 'smooth', block: 'start'})));
    });
    [...shown].forEach(k => {if (!keep.has(k)) shown.delete(k);});
    if (!list.children.length) list.append(el('li', 'amc-wx-line amc-wx-line--empty', 'Your selections appear here as you choose.'));
    builder.dataset.amcWxNext = open ? open.dataset.amcCategory : 'equip';
    builder.dataset.amcWxDone = String(doneCount);
    builder.dataset.amcWxTotal = String(live.length);
    drawPlan();
    updateBuild();
    saveBuild();
  }

  function renderEquipSummary() {}

  /* ---------- build totals ---------- */
  function configuredCents() {
    const dd = builder?.querySelector('[data-amc-estimate-field="total"]');
    return parseMoney(dd?.textContent) - nfdaSaving();
  }
  function nfdaSaving() {
    const base = builder?.querySelector('[data-amc-estimate-field="base"]');
    return window.AMCNfda?.savings(info?.dataset.productId, parseMoney(base?.textContent)) || 0;
  }
  function updateBuild() {
    if (!builder) return;
    const eq = equipmentTotal(), conf = configuredCents();
    const quantityInput = info?.querySelector('input[name="quantity"]:not([type="hidden"])');
    const quantity = Number(quantityInput?.value || 1);
    const validQuantity = Number.isSafeInteger(quantity) && quantity > 0 && (!quantityInput || quantityInput.checkValidity());
    const grand = conf && validQuantity ? conf * quantity + eq : 0;
    if (summary) summary.querySelector('[data-wx="total"]').textContent = grand ? fmt(grand) : '';
    const saving = summary?.querySelector('[data-wx="saving"]'), off = nfdaSaving() * quantity;
    if (saving) { saving.hidden = !off; saving.textContent = off ? `NFDA savings included: −${fmt(off)} (5% off walk-in base)` : ''; }
    const pb = info?.querySelector('[data-amc-wx-price]');
    if (pb) {
      pb.querySelector('[data-wx="p-total"]').textContent = grand ? fmt(grand) : (info.querySelector('[data-amc-main-planning-price]')?.textContent || '');
      pb.querySelector('.amc-wx-price__note').textContent = `${off ? 'NFDA 5% base-unit savings included automatically. ' : ''}Shipping and tax calculated at checkout.`;
    }
    // Finance the same priced build and equipment quantities that cart compilation uses.
    // Never multiply equipment quantities again when the cooler quantity changes.
    const financingCents = grand ? String(grand) : '';
    if (info && info.dataset.amcFinancingBuildCents !== financingCents) {
      info.dataset.amcFinancingBuildCents = financingCents;
      info.dispatchEvent(new CustomEvent('amc:financing-total-change', {bubbles:true}));
    }
    renderEquipSummary();
    if (bar) {
      const ready = builder.dataset.amcWxNext === 'equip' || builder.dataset.amcWxNext === undefined;
      const done = Number(builder.dataset.amcWxDone || 0), total = Number(builder.dataset.amcWxTotal || 0);
      bar.querySelector('[data-wx="bar-total"]').textContent = grand ? fmt(grand) : '';
      bar.querySelector('[data-wx="bar-status"]').textContent = ready
        ? (equipmentCount() ? `Build ready · ${equipmentCount()} equipment ${equipmentCount() === 1 ? 'item' : 'items'}` : 'Build ready')
        : `${done} of ${total} choices made`;
      const cta = bar.querySelector('[data-wx="bar-cta"]');
      const cart = builder.querySelector('.amc-walkin-cart');
      cta.textContent = ready ? (cart?.disabled ? (cart.textContent || 'Review build') : 'Add entire build to cart') : 'Continue your build';
      cta.dataset.mode = ready && !cart?.disabled ? 'cart' : 'next';
    }
  }

  function buildSummary() {
    const estimate = builder.querySelector('.amc-walkin-estimate');
    if (estimate) {estimate.classList.add('amc-wx-native-est'); hide(estimate);}
    // One clear message per area: hide the bridge's stacked helper copy (still updated for screen readers via status).
    hide(builder.querySelector('.amc-walkin-configurator__intro'));
    hide(builder.querySelector('.amc-walkin-cart-guidance'));
    hide(builder.querySelector('.amc-walkin-unpriced-note'));
    summary = buildBox;
    buildActions();
    const mainPrice = info.querySelector('.amc-configured-main-price');
    if (mainPrice && !mainPrice.querySelector('[data-amc-wx-price]')) {
      mainPrice.classList.add('amc-wx-mainprice');
      const label = mainPrice.querySelector('.amc-configured-main-price__label');
      if (label) label.textContent = 'Your build price';
      const box = el('div', 'amc-wx-price');
      box.dataset.amcWxPrice = '';
      box.innerHTML = `<strong class="amc-wx-price__total" data-wx="p-total"></strong><p class="amc-wx-price__note">Shipping and tax calculated at checkout.</p>`;
      mainPrice.append(box);
    }
  }

  /* One clean purchase row: add the build to the cart or to the quote */
  function buildActions() {
    const cartBtn = builder.querySelector('.amc-walkin-cart');
    if (!cartBtn || builder.querySelector('.amc-wx-actions')) return;
    const row = el('div', 'amc-wx-actions');
    cartBtn.before(row);
    row.append(cartBtn);
    const qbox = info.querySelector('[data-amc-qbtn]');
    if (qbox && !row.contains(qbox)) {
      const helper = [...(qbox.parentElement?.children || [])].find(n => n !== qbox && n.tagName === 'P' && /pdf/i.test(n.textContent));
      if (helper) hide(helper);
      qbox.classList.add('amc-wx-quotebtn');
      row.append(qbox);
    }
    // the drawer and the quote page cover formal quote requests, so the extra link is not needed here
    hide(builder.querySelector('.amc-walkin-configurator__quote'));
  }

  /* ---------- live top-view layout ---------- */
  const dims = (() => {
    const m = (root.dataset.productHandle || '').match(/(\d{1,2})x(\d{1,2})/);
    return m ? [Number(m[1]), Number(m[2])] : null;
  })();
  function layoutState() {
    const get = id => builder.querySelector('#' + id);
    const txt = s => (s && !isPlaceholderOpt(s.selectedOptions[0]) && !s.closest('[hidden]')) ? s.selectedOptions[0].textContent : '';
    const loc = get('bLoc'), door = get('bDoor'), hinge = get('bHinge'), floor = get('bFloor') || get('f'), ramp = get('bRamp'), led = get('bLed') || get('l'), plate = get('bPlate');
    const locText = txt(loc), locIdx = loc && !isPlaceholderOpt(loc.selectedOptions[0]) ? [...loc.options].filter(o => !isPlaceholderOpt(o)).indexOf(loc.selectedOptions[0]) : -1;
    return {
      locText, locIdx,
      corner: /corner/i.test(locText),
      wallFt: Number((locText.match(/(\d{1,2})\s*(?:′|'|-?\s*ft|-?\s*foot|\s*wall)/i) || [])[1] || 0),
      sideWord: /side/i.test(locText) ? 'side' : /front/i.test(locText) ? 'front' : '',
      wide: /48/.test(txt(door)),
      doorSet: !!txt(door),
      hingeSet: !!txt(hinge),
      left: /left/i.test(txt(hinge)),
      floor: !!txt(floor) && !/^no\b/i.test(txt(floor).trim()),
      ramp: !!txt(ramp) && !/^no\b/i.test(txt(ramp).trim()) && ramp.value !== 'no',
      led: !!txt(led) && !/^no\b/i.test(txt(led).trim()),
      ledN: ledCount(led) || (!!txt(led) && !/^no\b/i.test(txt(led).trim()) ? Number((txt(led).match(/(\d+)\s*×/) || [])[1] || 2) : 0),
      plate: !!txt(plate) && !/^no\b/i.test(txt(plate).trim()),
      lock: /lock/i.test(txt(get('bHandle')))
    };
  }
  let prevLook = {plate: true, lock: true};
  function drawPlan(previewLed) {
    if (!plan || !dims) return;
    const s = layoutState();
    if (previewLed) s.ledN = previewLed;
    const [a, b] = dims;
    const H = Math.max(a, b), V = Math.min(a, b); // horizontal = longer side
    const scale = Math.min(300 / H, 180 / V);
    const W = H * scale, D = V * scale, ox = 50, oy = 34;
    const t = Math.max(5, (4 / 12) * scale); // insulated panel wall thickness
    // which wall gets the door
    let wall = 'front';
    if (s.sideWord) wall = s.sideWord === 'side' ? 'side' : 'front';
    else if (s.wallFt) {
      if (a === b) wall = s.locIdx % 2 === 0 ? 'front' : 'side';
      else wall = s.wallFt === H ? 'front' : 'side';
    }
    const doorFt = s.wide ? 4 : 3, dw = doorFt * scale;
    const wallLen = wall === 'front' ? W : D;
    // door position comes only from the location choice; hinge only changes the swing
    const startAlong = s.corner ? wallLen - dw - 0.5 * scale : (wallLen - dw) / 2;
    const placed = !!s.locText;
    let door = '', swing = '', rampSvg = '', dims2 = '';
    const navy = '#1d2a4a', accent = '#0b5cad';
    if (placed) {
      if (wall === 'front') {
        const x = ox + startAlong, y = oy + D + t;
        const hx = s.left ? x : x + dw; // hinge point
        const ex = s.left ? x + dw : x;
        door = `<rect x="${x}" y="${y - t - 1}" width="${dw}" height="${t + 2}" fill="#fbfcfe"/><rect x="${x - 2}" y="${y - t - 1}" width="2.5" height="${t + 2}" fill="#8a97ad"/><rect x="${x + dw - 0.5}" y="${y - t - 1}" width="2.5" height="${t + 2}" fill="#8a97ad"/><rect class="amc-wx-plan__leaf" x="${hx - 2}" y="${y}" width="4" height="${dw}" rx="1" fill="#eef2f7" stroke="${accent}" stroke-width="1.6"/><circle cx="${hx}" cy="${y + 1.5}" r="2.2" fill="${navy}"/><rect x="${hx + (s.left ? 2 : -5)}" y="${y + dw * 0.78 - 4}" width="3" height="8" rx="1.2" fill="#6b778c"/>`;
        swing = s.hingeSet ? `<path d="M ${ex} ${y} A ${dw} ${dw} 0 0 ${s.left ? 1 : 0} ${hx} ${y + dw}" fill="none" stroke="${accent}" stroke-dasharray="3 3" stroke-width="1.4"/>` : '';
        if (s.ramp) rampSvg = `<path d="M ${x - 2} ${y + 2} L ${x + dw + 2} ${y + 2} L ${x + dw + 10} ${y + 2 + 34} L ${x - 10} ${y + 2 + 34} Z" fill="url(#amcWxDiamond)" stroke="#8a97ad" stroke-width="1"/>`;
      } else {
        const x = ox + W + t, y = oy + startAlong;
        const hy = s.left ? y + dw : y;
        const ey = s.left ? y : y + dw;
        door = `<rect x="${x - t - 1}" y="${y}" width="${t + 2}" height="${dw}" fill="#fbfcfe"/><rect x="${x - t - 1}" y="${y - 2}" width="${t + 2}" height="2.5" fill="#8a97ad"/><rect x="${x - t - 1}" y="${y + dw - 0.5}" width="${t + 2}" height="2.5" fill="#8a97ad"/><rect class="amc-wx-plan__leaf" x="${x}" y="${hy - 2}" width="${dw}" height="4" rx="1" fill="#eef2f7" stroke="${accent}" stroke-width="1.6"/><circle cx="${x + 1.5}" cy="${hy}" r="2.2" fill="${navy}"/><rect x="${x + dw * 0.78 - 4}" y="${hy + (s.left ? -5 : 2)}" width="8" height="3" rx="1.2" fill="#6b778c"/>`;
        swing = s.hingeSet ? `<path d="M ${x} ${ey} A ${dw} ${dw} 0 0 ${s.left ? 1 : 0} ${x + dw} ${hy}" fill="none" stroke="${accent}" stroke-dasharray="3 3" stroke-width="1.4"/>` : '';
        if (s.ramp) rampSvg = `<path d="M ${x + 2} ${y - 2} L ${x + 2} ${y + dw + 2} L ${x + 36} ${y + dw + 10} L ${x + 36} ${y - 10} Z" fill="url(#amcWxDiamond)" stroke="#8a97ad" stroke-width="1"/>`;
      }
    }
    // diamond plate: thick red band on the exterior face of the open door; lockable handle: shield at the latch
    let extras = '';
    if (placed) {
      const red = '#c8102e';
      const shield = (cx, cy) => `<g class="amc-wx-plan__lock${prevLook.lock ? '' : ' is-pop'}"><path d="M ${cx} ${cy - 8} L ${cx + 7} ${cy - 5} V ${cy + 0.5} C ${cx + 7} ${cy + 5} ${cx + 3.5} ${cy + 7.5} ${cx} ${cy + 9} C ${cx - 3.5} ${cy + 7.5} ${cx - 7} ${cy + 5} ${cx - 7} ${cy + 0.5} V ${cy - 5} Z" fill="${navy}" stroke="#fff" stroke-width="1.5"/><circle cx="${cx}" cy="${cy - 0.5}" r="1.8" fill="#fff"/><rect x="${cx - 0.8}" y="${cy}" width="1.6" height="4" rx=".6" fill="#fff"/></g>`;
      if (wall === 'front') {
        const x = ox + startAlong, y = oy + D + t, hx = s.left ? x : x + dw, off = s.left ? -3.5 : 3.5;
        if (s.lock) extras += shield(hx + off * 3.4, y + dw * 0.78);
      } else {
        const x = ox + W + t, y = oy + startAlong, hy = s.left ? y + dw : y, off = s.left ? 3.5 : -3.5;
        if (s.lock) extras += shield(x + dw * 0.78, hy + off * 3.4);
      }
    }
    // 4-ft fixtures drawn to scale and spread evenly across the ceiling
    let lights = '';
    const ledFresh = s.ledN !== drawPlan.lastLed; if (!previewLed) drawPlan.lastLed = s.ledN;
    if (s.ledN > 0) {
      const inset = 1, top = (wall === 'front' ? 2 : 1), left = (wall === 'front' ? 1 : 2);
      const iw = H - left - inset, idp = V - top - inset;
      let maxC = 1; while (maxC < 12 && (iw - 4 * (maxC + 1)) / (maxC + 2) >= 1.5) maxC++;
      let best = null;
      for (let c = 1; c <= Math.min(s.ledN, maxC); c++) {
        const r = Math.ceil(s.ledN / c), score = Math.abs(iw / c - idp / r) + (r * c - s.ledN) * 0.6;
        if (!best || score < best.score) best = {c, r, score};
      }
      const len = 4 * scale, th = Math.max(5, 0.45 * scale);
      for (let i = 0; i < s.ledN; i++) {
        const row = Math.floor(i / best.c), inRow = row === best.r - 1 ? s.ledN - row * best.c : best.c, col = i % best.c;
        const cx = ox + (left + iw * (col + 0.5) / inRow) * scale, cy = oy + (top + idp * (row + 0.5) / best.r) * scale;
        lights += `<g class="amc-wx-plan__led${ledFresh ? '' : ' is-still'}"><ellipse cx="${cx}" cy="${cy}" rx="${len * 0.8}" ry="${Math.max(th * 2.6, scale * 1.6)}" fill="url(#amcWxGlow)"/><rect x="${cx - len / 2}" y="${cy - th / 2}" width="${len}" height="${th}" rx="1.5" fill="#fff6d6" stroke="#b8922c" stroke-width=".9"/><line x1="${cx - len / 2 + 2}" y1="${cy}" x2="${cx + len / 2 - 2}" y2="${cy}" stroke="#f2c94c" stroke-width="${Math.max(1.2, th * 0.35)}" stroke-linecap="round"/></g>`;
      }
    }
    const evL = Math.min(Math.max(3.5 * scale, 54), (wall === 'front' ? W : D) * 0.5), evT = Math.max(12, 1.1 * scale);
    const fans = n => Array.from({length: n}, (_, i) => (i + 0.5) / n);
    const fan = (cx, cy, r) => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#f7f9fc" stroke="#7d8aa1" stroke-width=".9"/><path d="M ${cx - r * 0.7} ${cy} L ${cx + r * 0.7} ${cy} M ${cx} ${cy - r * 0.7} L ${cx} ${cy + r * 0.7}" stroke="#9aa6ba" stroke-width=".8"/><circle cx="${cx}" cy="${cy}" r="${r * 0.22}" fill="#7d8aa1"/>`;
    const nf = evL > 80 ? 2 : 1, fr = Math.min(evT * 0.36, 7);
    const evap = wall === 'front'
      ? `<g><rect x="${ox + W / 2 - evL / 2}" y="${oy + 2}" width="${evL}" height="${evT}" rx="2" fill="#e3e8f0" stroke="#7d8aa1"/>${fans(nf).map(f => fan(ox + W / 2 - evL / 2 + evL * f, oy + 2 + evT / 2, fr)).join('')}<text x="${ox + W / 2}" y="${oy + evT + 13}" text-anchor="middle" class="amc-wx-plan__lbl">Evaporator</text></g>`
      : `<g><rect x="${ox + 2}" y="${oy + D / 2 - evL / 2}" width="${evT}" height="${evL}" rx="2" fill="#e3e8f0" stroke="#7d8aa1"/>${fans(nf).map(f => fan(ox + 2 + evT / 2, oy + D / 2 - evL / 2 + evL * f, fr)).join('')}<text x="${ox + evT + 8}" y="${oy + D / 2 + 3}" class="amc-wx-plan__lbl">Evaporator</text></g>`;
    // The illustration is schematic. Never label nominal model sizes as
    // measured walls, or show an inaccurate scale derived from a model name.
    dims2 = '';
    // 4-ft scale bar (the length of one LED fixture)
    const sbY = oy + D + t + (wall === 'front' && placed ? Math.max(dw, s.ramp ? 38 : 0) + 8 : 14);
    // door face (front view) so the plate and handle read clearly
    let face = '';
    if (placed && (s.plate || s.lock)) {
      const fw = s.wide ? 44 : 34, fh = 68, fx = ox + W + t + Math.max(dw, s.ramp ? 38 : 0) + 22, fy = Math.max(4, oy + D + t - fh), red = '#c8102e';
      const hxF = s.left ? fx + fw - 6 : fx + 3;
      face = `<g class="amc-wx-plan__face"><rect x="${fx - 3}" y="${fy - 3}" width="${fw + 6}" height="${fh + 3}" rx="2" fill="#8a97ad"/><rect x="${fx}" y="${fy}" width="${fw}" height="${fh}" fill="#eef2f7" stroke="${navy}" stroke-width="1"/>` +
        (s.plate ? `<rect class="amc-wx-plan__plate${prevLook.plate ? '' : ' is-pop'}" x="${fx + 1.5}" y="${fy + fh * 0.62}" width="${fw - 3}" height="${fh * 0.38 - 1.5}" fill="url(#amcWxPlateRed)" stroke="${red}" stroke-width="1.2"/>` : '') +
        `<rect x="${hxF}" y="${fy + fh * 0.42}" width="3" height="9" rx="1.2" fill="#6b778c"/>` +
        (s.lock ? `<g transform="translate(${hxF + 1.5 + (s.left ? -9 : 9)} ${fy + fh * 0.47}) scale(.7)"><path d="M 0 -8 L 7 -5 V .5 C 7 5 3.5 7.5 0 9 C -3.5 7.5 -7 5 -7 .5 V -5 Z" fill="${navy}" stroke="#fff" stroke-width="1.5"/><circle cx="0" cy="-.5" r="1.8" fill="#fff"/></g>` : '') +
        `<text x="${fx + fw / 2}" y="${fy + fh + 11}" text-anchor="middle" class="amc-wx-plan__lbl">Door face</text>` +
        (s.plate ? `<text x="${fx + fw / 2}" y="${fy + fh + 22}" text-anchor="middle" class="amc-wx-plan__lbl" style="fill:${red}">ADT plate</text>` : '') + `</g>`;
    }
    prevLook = {plate: s.plate, lock: s.lock};
    const sbY2 = sbY;
    // No scale bar: final installation and usable clearances require the
    // model-specific factory drawing, including selected equipment.
    const floorFill = s.floor ? 'url(#amcWxFloor)' : '#fbfcfe';
    // reserve room for the door leaf, swing arc and ramp on both the front and side walls
    const reach = Math.max(dw, s.ramp ? 38 : 0) + 14 + t;
    const svgW = ox + W + reach + (face ? (s.wide ? 44 : 34) + 30 : 0), svgH = Math.max(oy + D + reach, sbY2 + 10);
    plan.querySelector('[data-wx="plan-svg"]').innerHTML = `<svg viewBox="0 0 ${svgW} ${svgH}" role="img" aria-label="Top view of your ${H} by ${V} foot ${unit}${placed ? `, door on the ${wall === 'front' ? H : V} foot wall` : ''}"><defs><pattern id="amcWxFloor" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="8" height="8" fill="#f1f4f9"/><line x1="0" y1="0" x2="0" y2="8" stroke="#dde3ec" stroke-width="2"/></pattern><pattern id="amcWxDiamond" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="7" height="7" fill="#dfe4ec"/><rect x="2" y="1" width="3" height="1.4" rx=".7" fill="#aeb8c8"/></pattern><pattern id="amcWxPanel" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#3a4a66"/><circle cx="2" cy="2" r=".6" fill="#53637f"/></pattern><pattern id="amcWxPlateRed" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="5" height="5" fill="#f6d9dd"/><rect x="1.3" y="1.6" width="2.4" height="1.1" rx=".5" fill="#c8102e" opacity=".75"/></pattern><radialGradient id="amcWxGlow"><stop offset="0" stop-color="#ffe9a3" stop-opacity=".75"/><stop offset="1" stop-color="#ffe9a3" stop-opacity="0"/></radialGradient></defs><rect x="${ox - t}" y="${oy - t}" width="${W + 2 * t}" height="${D + 2 * t}" fill="url(#amcWxPanel)" stroke="${navy}" stroke-width="1.2" rx="3"/><rect x="${ox}" y="${oy}" width="${W}" height="${D}" fill="${floorFill}" stroke="#53637f" stroke-width=".8"/>${evap}${lights}${rampSvg}${door}${swing}${extras}${face}${dims2}</svg>`;
    renderDims();
    const cap = plan.querySelector('[data-wx="plan-cap"]');
    if (cap) cap.textContent = `Nominal ${a}′ × ${b}′ model · schematic, not to scale${placed ? ` · ${wall} wall door` : ''}`;
    plan.querySelector('svg')?.setAttribute('aria-label', `Schematic top view of nominal ${a} by ${b} ${unit}, not to scale${placed ? `, door on the ${wall} wall` : ''}`);
    let key = plan.querySelector('[data-wx="plan-key"]');
    if (!key && cap) {key = document.createElement('div'); key.className = 'amc-wx-plan__key'; key.dataset.wx = 'plan-key'; cap.after(key);}
    if (key) {const k = []; if (placed && (s.plate || s.lock)) k.push('<span><i class="amc-wx-plan__sw amc-wx-plan__sw--door"></i>Door front view</span>'); if (placed && s.plate) k.push('<span><i class="amc-wx-plan__sw amc-wx-plan__sw--plate"></i>ADT diamond plate · lower door</span>'); if (placed && s.lock) k.push('<span><i class="amc-wx-plan__sw amc-wx-plan__sw--lock"></i>Lockable handle</span>'); key.innerHTML = k.join(''); key.hidden = !k.length;}
  }
  let dimRows = [];
  try {
    dimRows = (JSON.parse(root.dataset.dims || '[]') || []).map(row => {
      if (typeof row !== 'string') return row;
      const at = row.indexOf(':');
      return {label:at < 0 ? row : row.slice(0,at),value:at < 0 ? '' : row.slice(at + 1).trim()};
    }).filter(row => row && typeof row.label === 'string');
  } catch (_) {dimRows = [];}
  const dimVal = re => (dimRows.find(r => re.test(r.label || '')) || {}).value || '';
  function renderDims() {
    const box = plan && plan.querySelector('[data-wx="dims"]');
    if (!box) return;
    const ext = dimVal(/^exterior dim/i), extH = dimVal(/^exterior height/i), int = dimVal(/^interior/i);
    if (!ext && !int) {box.hidden = true; return;}
    // exterior height follows the floor choice when both heights are published
    let h = extH;
    const floorless = extH.match(/([\d.]+)″\s*floorless/i), floored = extH.match(/([\d.]+)″\s*with insulated floor/i);
    if (floorless && floored) {
      const f = builder && builder.querySelector('#bFloor');
      const opt = f && f.selectedOptions[0];
      const hasFloor = opt && !isPlaceholderOpt(opt) && !/^\s*no\b/i.test(opt.textContent);
      h = `${hasFloor ? floored[1] : floorless[1]}″ H`;
    }
    const extLine = ext ? `${ext}${h ? ` × ${h}` : ''}` : '';
    box.innerHTML = (extLine ? `<div><dt>Exterior</dt><dd>${esc(extLine)}</dd></div>` : '') + (int ? `<div><dt>Interior</dt><dd>${esc(int.replace(/\s*\(approx\.?\)/i, ''))}${/approx/i.test(dimRows.find(r => /^interior/i.test(r.label || ''))?.label || '') ? ' <span>approx.</span>' : ''}</dd></div>` : '');
    box.hidden = false;
  }
  function buildPlan() {
    if (!dims) return;
    plan = el('figure', 'amc-wx-plan');
    plan.innerHTML = `<div class="amc-wx-plan__svg" data-wx="plan-svg"></div><figcaption data-wx="plan-cap"></figcaption><dl class="amc-wx-dims" data-wx="dims" hidden></dl>`;
    placePlan();
  }
  function placePlan() {
    if (!plan || !buildBox) return;
    buildBox.querySelector('.amc-wx-build__head')?.append(plan);
  }

  /* ---------- sticky build bar ---------- */
  function buildBar() {
    bar = el('div', 'amc-wx-bar');
    bar.setAttribute('aria-label', 'Your build');
    const img = root.dataset.image;
    bar.innerHTML = `<div class="amc-wx-bar__in page-width">${img ? `<img class="amc-wx-bar__img" src="${esc(img)}" alt="" width="56" height="42">` : ''}<div class="amc-wx-bar__txt"><strong>${esc(productTitle)}</strong><span data-wx="bar-status"></span></div><div class="amc-wx-bar__total"><span>Build total</span><strong data-wx="bar-total"></strong></div><button type="button" class="amc-wx-bar__cta" data-wx="bar-cta"></button></div>`;
    document.body.append(bar);
    bar.querySelector('[data-wx="bar-cta"]').addEventListener('click', e => {
      const cta = e.currentTarget;
      if (cta.dataset.mode === 'cart') {
        // Use the bridge's validated entry point directly. A synthetic click can
        // be swallowed by storefront click handlers before reaching the bridge.
        if (typeof info.amcAddConfiguredBuild === 'function') {
          info.amcAddConfiguredBuild(e);
        } else {
          const cart = builder.querySelector('.amc-walkin-cart');
          if (cart && !cart.disabled) cart.click();
        }
        return;
      }
      const open = builder.querySelector('.amc-wx-q.is-open') || builder.querySelector('.amc-walkin-cart');
      open?.scrollIntoView({behavior: 'smooth', block: 'center'});
      setTimeout(() => open?.querySelector?.('.amc-wx-tile:not(:disabled)')?.focus({preventScroll: true}), 450);
    });
    const cart = builder.querySelector('.amc-walkin-cart');
    const gallery = info.querySelector('.product__media-wrapper');
    let cartSeen = false, past = false;
    const sync = () => bar.classList.toggle('is-on', past && !cartSeen);
    if ('IntersectionObserver' in window) {
      if (cart) new IntersectionObserver(([en]) => {cartSeen = en.isIntersecting; sync();}).observe(cart);
      const top = info.querySelector('.product__title') || gallery;
      if (top) new IntersectionObserver(([en]) => {past = !en.isIntersecting && en.boundingClientRect.top < 0; sync();}).observe(top);
    }
  }

  /* ---------- product information: no duplicate tables, compact grid ---------- */
  function tidyInfo() {
    const pi = document.querySelector('.amc-product-information');
    if (!pi) return;
    pi.classList.add('amc-wx-pi');
    pi.querySelectorAll('.amc-pi__panel').forEach(panel => {
      const mig = panel.querySelector(':scope > .amc-pi__migrated');
      const wraps = [...panel.querySelectorAll(':scope > .amc-pi__table-wrap')];
      if (!mig) return;
      const migTable = mig.querySelector(':scope > .responsive-table, :scope > table');
      if (/-specifications$/.test(panel.id) && migTable && wraps.length) {
        // structured key-spec table is kept; the copied one from the description is hidden
        const h = migTable.previousElementSibling;
        if (h && /^H[23]$/.test(h.tagName)) h.classList.add('amc-wx-hide');
        migTable.classList.add('amc-wx-hide');
        panel.classList.add('amc-wx-pi-grid');
      }
      if (/-dimensions$/.test(panel.id) && migTable && wraps.length) {
        wraps.forEach(w => w.classList.add('amc-wx-hide'));
        const h = migTable.previousElementSibling;
        if (h && /^H[23]$/.test(h.tagName)) h.classList.add('amc-wx-hide');
      }
    });
    // some theme heading sizes sit in cascade layers, so the compact sizes are set inline
    pi.querySelectorAll('.amc-pi__copy h2, .amc-pi__copy h3, .amc-pi__migrated h2, .amc-pi__migrated h3').forEach(h => {h.style.setProperty('font-size', '18px', 'important'); h.style.setProperty('line-height', '1.3', 'important'); h.style.setProperty('margin', '4px 0 8px', 'important');});
    const head = pi.querySelector('.amc-pi__heading'); if (head) head.style.setProperty('font-size', '22px', 'important');
    const copy = pi.querySelector('[id$="-details"] .amc-pi__copy');
    if (copy && !copy.dataset.wxGrid) {
      copy.dataset.wxGrid = '1';
      const kids = [...copy.children];
      const h = kids.find(k => /^H[23]$/.test(k.tagName));
      if (h) {
        const col = document.createElement('div'); col.className = 'amc-wx-pi-col';
        let n = h; const grab = [];
        while (n && !(n.tagName === 'P' && grab.length > 1)) {grab.push(n); n = n.nextElementSibling;}
        h.before(col); grab.forEach(g => col.append(g));
        const rest = document.createElement('div'); rest.className = 'amc-wx-pi-col';
        while (col.nextElementSibling) rest.append(col.nextElementSibling);
        if (rest.children.length) {col.after(rest); copy.classList.add('amc-wx-pi-split');}
      }
    }
  }

  /* ---------- optional equipment after the build ---------- */
  function placeStrip() {
    const tpl = root.querySelector('[data-amc-wx-strip-template]');
    if (!tpl || document.querySelector('.amc-wx-strip')) return;
    if (!items.some(i => i.strip)) return;
    const node = tpl.content.firstElementChild.cloneNode(true);
    root.prepend(node);
    node.querySelector('.amc-wx-strip__all').addEventListener('click', e => {e.preventDefault(); root.scrollIntoView({behavior: 'smooth', block: 'start'});});
  }


  /* ---------- remember the build in this browser ---------- */
  const BUILD_STORE = 'amcWxBuild:' + (root.dataset.productHandle || '');
  let restoring = false, restored = false;
  function saveBuild() {
    if (!builder || restoring || !restored) return;
    const values = {};
    ORDER.forEach(id => {const sel = builder.querySelector('#' + id); if (sel && !isPlaceholderOpt(sel.selectedOptions[0])) values[id] = sel.value;});
    try {
      if (!Object.keys(values).length && !confirmed.size) localStorage.removeItem(BUILD_STORE);
      else localStorage.setItem(BUILD_STORE, JSON.stringify({t: Date.now(), values, confirmed: [...confirmed]}));
    } catch (_) {}
  }
  async function restoreBuild() {
    let saved = null;
    try {saved = JSON.parse(localStorage.getItem(BUILD_STORE) || 'null');} catch (_) {}
    if (!saved || !saved.values || Date.now() - (saved.t || 0) > 30 * 864e5) {restored = true; return;}
    restoring = true;
    for (const id of ORDER) {
      const sel = builder.querySelector('#' + id), v = saved.values[id];
      if (!sel || v === undefined) continue;
      const idx = [...sel.options].findIndex(o => o.value === v && !o.disabled && !isPlaceholderOpt(o));
      if (idx < 0) continue;
      if (sel.selectedIndex !== idx) {
        sel.selectedIndex = idx;
        sel.dispatchEvent(new Event('input', {bubbles: true}));
        sel.dispatchEvent(new Event('change', {bubbles: true}));
      }
      await new Promise(r => setTimeout(r, 40));
    }
    (saved.confirmed || []).forEach(c => confirmed.add(c));
    restoring = false; restored = true;
    refresh();
  }
  function forgetBuild() {
    try {localStorage.removeItem(BUILD_STORE);} catch (_) {}
    confirmed.clear(); openCat = null;
    setTimeout(refresh, 150);
  }

  /* ---------- quote: carry the whole build ---------- */
  function addBuildToQuote(btn) {
    const Q = window.AMCQuote, B = window.AMCWalkinBridge;
    if (!Q || !B || !builder) return false;
    const box = btn.closest('[data-amc-qbtn]');
    const label = btn.querySelector('span');
    const say = t => {if (label) label.textContent = t;};
    const restore = () => setTimeout(() => {btn.classList.remove('is-added'); say(box?.dataset.label || 'Add to Quote');}, 2600);
    if (!B.valid(builder, true)) {
      refresh();
      builder.querySelector('.amc-wx-q.is-open')?.scrollIntoView({behavior: 'smooth', block: 'center'});
      setTimeout(focusOpen, 450);
      say('Finish your build first'); restore();
      return true;
    }
    let pdata = {};
    try {pdata = JSON.parse(box.querySelector('script[type="application/json"]').textContent);} catch (_) {}
    const details = B.details(builder);
    let conf = null;
    try {conf = B.configuration(builder);} catch (_) {conf = null;}
    const qty = conf ? conf.items[0].quantity : 1;
    const props = {};
    if (conf) Object.entries(conf.properties).forEach(([k, v]) => {if (k.charAt(0) !== '_' && k !== 'Cooler configuration') props[k] = v;});
    else details.selections.forEach(line => {const i = line.indexOf(':'); if (i > 0 && !/Not applicable/i.test(line)) props[line.slice(0, i).trim()] = line.slice(i + 1).trim();});
    props['Configured price'] = fmt(configuredCents());
    let components = null;
    if (conf) {
      const m = new Map();
      conf.items.forEach(it => {const k = String(it.id); const prev = m.get(k); m.set(k, {id: k, qty: (prev ? prev.qty : 0) + it.quantity / qty, price: it.expectedPrice});});
      components = {items: [...m.values()], properties: conf.properties};
    }
    const base = pdata.variants?.[0] || {};
    Q.add({
      variantId: conf ? conf.items[0].id : base.id, productId: pdata.id, handle: pdata.handle, title: productTitle,
      variantTitle: 'Configured build', sku: base.sku || '', vendor: pdata.vendor,
      price: configuredCents(), qty, available: true, quoteOnly: !conf,
      url: '/products/' + (pdata.handle || ''), image: pdata.featured_image || root.dataset.image || '',
      properties: props, amcBuild: components, amcPrice: configuredCents()
    });
    equipmentLines().forEach(l => Q.add({
      variantId: l.variant.id, handle: l.item.handle || '', title: l.item.title || l.item.name,
      variantTitle: l.variant.title, sku: l.variant.sku || '', price: l.variant.price, qty: l.qty, available: true,
      url: l.item.url, image: l.item.image || '', properties: {'Added with': productTitle}
    }));
    btn.classList.add('is-added');
    say(equipmentLines().length ? 'Build and equipment added to quote' : 'Build added to your quote');
    if (typeof Q.open === 'function') Q.open(`Added your ${productTitle} build${equipmentLines().length ? ' and equipment' : ''}.`);
    else {const msg = box.querySelector('[data-amc-qmsg]'); if (msg) msg.hidden = false;}
    restore();
    return true;
  }
  document.addEventListener('click', e => {
    const btn = e.target.closest?.('[data-amc-qadd]');
    if (!btn || !info || btn.closest('product-info') !== info || btn.closest('.amc-wx-card')) return;
    if (!window.AMCQuote || !window.AMCWalkinBridge) return;
    e.preventDefault(); e.stopImmediatePropagation();
    addBuildToQuote(btn);
  }, true);
  /* PO / RFQ: same full build (configured price, every selection, equipment lines). Starts a PO / RFQ automatically if none exists. */
  function addBuildToPo(btn) {
    const Q = window.AMCQuote, B = window.AMCWalkinBridge;
    if (!Q || typeof Q.poAdd !== 'function' || !B || !builder) return false;
    const label = btn.querySelector('span');
    const say = t => {if (label) label.textContent = t;};
    const restore = () => setTimeout(() => {btn.classList.remove('is-added'); say('Add to PO / RFQ');}, 2200);
    if (!B.valid(builder, true)) {
      refresh();
      builder.querySelector('.amc-wx-q.is-open')?.scrollIntoView({behavior: 'smooth', block: 'center'});
      setTimeout(focusOpen, 450);
      say('Finish your build first'); restore();
      return true;
    }
    const box = btn.closest('[data-amc-qbtn]') || info;
    let pdata = {};
    try {pdata = JSON.parse(box.querySelector('script[type="application/json"]').textContent);} catch (_) {}
    const details = B.details(builder);
    let conf = null;
    try {conf = B.configuration(builder);} catch (_) {conf = null;}
    const props = {};
    if (conf) Object.entries(conf.properties).forEach(([k, v]) => {if (k.charAt(0) !== '_' && k !== 'Cooler configuration') props[k] = v;});
    else details.selections.forEach(line => {const i = line.indexOf(':'); if (i > 0 && !/Not applicable/i.test(line)) props[line.slice(0, i).trim()] = line.slice(i + 1).trim();});
    props['Configured price'] = fmt(configuredCents());
    const base = pdata.variants?.[0] || {};
    const eq = equipmentLines();
    const qty = conf ? conf.items[0].quantity : 1;
    const poItem = {
      variantId: conf ? conf.items[0].id : base.id, productId: pdata.id, handle: pdata.handle, title: productTitle,
      variantTitle: 'Configured build', sku: base.sku || '', vendor: pdata.vendor,
      price: configuredCents(), qty, available: true, quoteOnly: !conf,
      url: '/products/' + (pdata.handle || ''), image: pdata.featured_image || root.dataset.image || '',
      properties: props,
      amcBuild: conf ? {items:conf.items.map(c=>({id:String(c.id),qty:c.quantity/qty,price:c.expectedPrice})),properties:conf.properties} : undefined
    };
    Q.poAdd(window.AMCNfda ? window.AMCNfda.item(poItem) : poItem, `Added your ${productTitle} build${eq.length ? ' and equipment' : ''}.`);
    eq.forEach(l => Q.poAdd({
      variantId: l.variant.id, handle: l.item.handle || '', title: l.item.title || l.item.name,
      variantTitle: l.variant.title, sku: l.variant.sku || '', price: l.variant.price, qty: l.qty, available: true,
      url: l.item.url, image: l.item.image || '', properties: {'Added with': productTitle}
    }, `Added your ${productTitle} build and equipment.`));
    try {window.klaviyo?.track?.('Added to Purchase Order', {ProductName: productTitle, VariantName: 'Configured build', Price: configuredCents() / 100, Quantity: 1, URL: location.href});} catch (_) {}
    btn.classList.add('is-added'); say('Added to PO / RFQ'); restore();
    return true;
  }
  document.addEventListener('click', e => {
    const btn = e.target.closest?.('[data-amc-poadd]');
    if (!btn || !info || btn.closest('product-info') !== info || btn.closest('.amc-wx-card')) return;
    if (!window.AMCQuote || typeof window.AMCQuote.poAdd !== 'function' || !window.AMCWalkinBridge) return;
    e.preventDefault(); e.stopImmediatePropagation();
    addBuildToPo(btn);
  }, true);
  // The "Prefer an AMC quote?" request carries the selected equipment too.
  document.addEventListener('click', e => {
    const a = e.target.closest?.('.amc-walkin-configurator__quote');
    if (!a || !a.getAttribute('href') || e.defaultPrevented) return;
    const lines = equipmentLines();
    if (!lines.length) return;
    const u = new URL(a.getAttribute('href'), location.origin);
    const text = u.searchParams.get('configuration') || '';
    if (/Equipment added with this build:/.test(text)) return;
    const eq = lines.map(l => {const vl = l.item.variants.length > 1 && !/^Default/i.test(l.variant.title || '') ? l.variant.title : ''; return `• ${l.qty} × ${l.item.title || l.item.name}${vl ? ` (${vl})` : ''} — ${fmt(l.variant.price * l.qty)}`;});
    u.searchParams.set('configuration', `${text}\nEquipment added with this build:\n${eq.join('\n')}\nBuild total with equipment: ${fmt(configuredCents() + equipmentTotal())}`);
    a.setAttribute('href', u.pathname + u.search);
  });

  /* ---------- boot ---------- */
  function boot() {
    builder = document.querySelector('.amc-walkin-configurator[data-amc-bridge-ready="true"]');
    if (!builder) return false;
    info = builder.closest('product-info');
    if (!info || info.dataset.amcWx) return true;
    info.dataset.amcWx = 'true';
    document.documentElement.classList.add('amc-wx-on');
    info.classList.add('amc-wx-product');
    const heading = builder.querySelector('.amc-walkin-configurator__heading');
    if (heading) heading.textContent = isFreezer ? 'Build your walk-in freezer' : 'Build your walk-in cooler';
    const intro = builder.querySelector('.amc-walkin-configurator__intro');
    
    builder.querySelectorAll('small[id^="amc-option-help-"]').forEach(n => n.classList.add('amc-wx-hide'));
    // Moving the related "complete" section is replaced by the equipment section.
    document.querySelectorAll('.section-related-products:has(.amc-curated-products)').forEach(n => n.classList.add('amc-wx-hide'));
    const grid = builder.closest('.product') || info.querySelector('.product');
    if (grid && grid.parentElement && !root.contains(grid)) grid.after(root);
    root.classList.remove('amc-wx--side');
    root.hidden = true;
    root.style.setProperty('display', 'none', 'important');
    placeStrip();
    tidyInfo(); setTimeout(tidyInfo, 1500); setTimeout(tidyInfo, 4000);
    expandLed();
    buildFlow();
    buildSummary();
    buildPlan();
    buildBar();
    const siteNote = el('p', 'amc-wx-site-note');
    siteNote.textContent = root.dataset.siteNotice || 'Install on a solid, flat, level supporting surface. Even small irregularities can affect panel and door alignment. An insulated floor does not level the site. Customers are responsible for accurate measurements, access and site preparation. AMC is not responsible for issues caused by inaccurate customer measurements or an unsuitable or unlevel installation surface. Confirm the final factory drawing before site preparation.';
    builder.append(siteNote);
    renderEquipment();
    refresh();
    restoreBuild();
    builder.querySelector('#bReset')?.addEventListener('click', forgetBuild);
    builder.addEventListener('amc:configuration-updated', () => setTimeout(refresh, 0));
    ['input','change'].forEach(name => info.addEventListener(name, event => {
      if (event.target.matches?.('input[name="quantity"]')) updateBuild();
    }));
    builder.addEventListener('change', e => {if (e.target?.matches?.('#bFloor,#f,#bDoor')) enforceFloorDoor(); setTimeout(refresh, 0);});
    let t;
    new MutationObserver(records => {
      if (records.every(r => r.target.closest?.('.amc-wx-tiles, .amc-wx-qbox__top, .amc-wx-qbox__prog, .amc-wx-build, .amc-wx-done') || r.target.classList?.contains('amc-wx-tiles'))) return;
      clearTimeout(t); t = setTimeout(refresh, 30);
    }).observe(builder, {subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'disabled'], characterData: true});
    return true;
  }

  const startCart = () => {if (!wrapCart()) {let n = 0; const iv = setInterval(() => {if (wrapCart() || ++n > 100) clearInterval(iv);}, 100);}};
  startCart();
  renderEquipment();
  if (!boot()) {
    let n = 0;
    const iv = setInterval(() => {if (boot() || ++n > 120) clearInterval(iv);}, 100);
  }
})();
